import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ListPageTopBar } from "@/components/layout/dashboard-layout";
import { useTable } from "@/hooks/useTable";
import { ListFooter } from "@/components/shared/list-footer";

import { ErrorBoundary } from "react-error-boundary";
import { RecordNotFound } from "@/components/shared/record-not-found";
import { TableColumnsHandle } from "@/components/shared/table-columns-handle";
import { ArrowLeftIcon, Columns3Icon, FilterIcon, X } from "lucide-react";
import { useMutations } from "./utils/react-hook/use-mutations";
import { ReactForm, ReactTable, ReactTableSkelton } from "./utils";
import { Fragment } from "react/jsx-runtime";
import ErrorFallback from "@/components/shared/error-fallback";
import { ConditionRenderer } from "@/components/shared/condition-renderer";
import { useUserInfo } from "@/global-data/use-user-info";

export const Roles = () => {
  const {
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    skeltonLoading,
    tableColumn,
    setTableColumn,
    // companyList,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    currentUser,
    companyData,
    company_id,
  } = useMutations();

  const { tableHeader, table, tableKeys, onResize, onSorting } = useTable({
    data: listData,
    view: ["role_name", "description", "modified_by"],
    verifyKey: "roles",
    visible: tableColumn,
    avoid: ["user_id"],
  });

  const { activePermission } = useUserInfo();

  return (
    <Fragment>
      {formPopupOpen ? (
        <div className="flex flex-col gap-5">
          <ListPageTopBar
            title={activeRecordData ? "Update Role" : "Create Role"}
          >
            <Button variant={"action"} onClick={() => onFormOpenChange(false)}>
              <ArrowLeftIcon /> Back
            </Button>
          </ListPageTopBar>
          <Card className="pt-6">
            <ReactForm
              formData={activeRecordData}
              onFormSubmit={onFormSubmit}
              onOpenChange={onFormOpenChange}
              open={formPopupOpen}
              currentUser={currentUser}
              companyData={companyData}
              company_id={company_id}
            />
          </Card>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ListPageTopBar title="Roles">
            <TableColumnsHandle
              tableKeys={tableKeys}
              tableColumn={tableColumn}
              setTableColumn={setTableColumn}
              onSorting={onSorting}
              labels={
                {
                  // company_id: "Company ID",
                  // display_name: "Display Name",
                  // snc_company_id: "SNC Company ID",
                  // modified_by: "Modified By",
                }
              }
            >
              <Columns3Icon /> Column
            </TableColumnsHandle>
            <ConditionRenderer condition={Object.keys(filter).length !== 0}>
              <>
                <Button variant={"action"} onClick={filterRequest}>
                  <FilterIcon /> Filter
                </Button>
                <Button variant={"action"} onClick={clearFilter}>
                  <X /> Clear Filter
                </Button>
              </>
            </ConditionRenderer>
            {activePermission?.create ? (
              <Button variant={"action"} onClick={() => onFormOpenChange(true)}>
                + Create
              </Button>
            ) : null}
          </ListPageTopBar>

          {skeltonLoading ? (
            <ReactTableSkelton />
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="max-w-[100%] p-0 overflow-auto scrollbar h-auto min-h-[350px] md:h-[calc(100dvh-340px)]">
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }) => (
                    <ErrorFallback
                      error={error}
                      errorInfo={error.stack || ""}
                      resetErrorBoundary={resetErrorBoundary}
                    />
                  )}
                >
                  <ReactTable
                    tableHeader={tableHeader}
                    onResize={onResize}
                    table={table}
                    permission={permission}
                    onAction={onAction}
                    filter={filter}
                    setFilter={setFilter}
                    listData={listData}
                    filterRequest={filterRequest}
                  />
                  {table.length === 0 ? <RecordNotFound /> : null}
                </ErrorBoundary>
              </CardContent>
              <ErrorBoundary
                fallbackRender={({ error, resetErrorBoundary }) => (
                  <ErrorFallback
                    error={error}
                    errorInfo={error.stack || ""}
                    resetErrorBoundary={resetErrorBoundary}
                  />
                )}
              >
                <hr />
                <ListFooter
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                  page={paginationData.page}
                  perPage={paginationData.perPage}
                  totalItem={paginationData.totalItem}
                  className="px-5 py-5"
                  isEmpty={table.length === 0}
                />
              </ErrorBoundary>
            </Card>
          )}
        </div>
      )}
    </Fragment>
  );
};
